import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  buildPortalCustomerNotifications,
  getDismissedIds,
  dismissNotification as persistDismiss,
  dismissNotificationIds
} from './portalNotificationUtils';

function BellIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * @param {{ jobs: any[], inquiries: any[] }} props — inquiries should include status, preferred_date/time (or scheduled_*)
 */
export default function PortalNotificationBell({ jobs = [], inquiries = [], darkMode = false }) {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [dismissed, setDismissed] = useState(() => getDismissedIds());
  const panelRef = useRef(null);
  const prevIdsRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'portal_customer_notif_dismissed') setDismissed(getDismissedIds());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', onDoc);
      return () => document.removeEventListener('mousedown', onDoc);
    }
  }, [open]);

  const items = useMemo(
    () =>
      buildPortalCustomerNotifications({
        jobs,
        inquiries,
        now,
        dismissed
      }),
    [jobs, inquiries, now, dismissed]
  );

  const count = items.length;

  // Browser notifications when new items appear after load (optional)
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    const ids = new Set(items.map((i) => i.id));
    const prev = prevIdsRef.current;
    if (prev === null) {
      prevIdsRef.current = ids;
      return;
    }
    for (const it of items) {
      if (!prev.has(it.id) && Notification.permission === 'granted') {
        try {
          new Notification(it.title, { body: it.detail, tag: it.id });
        } catch (_) {}
      }
    }
    prevIdsRef.current = ids;
  }, [items]);

  const requestNotifyPermission = () => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
    Notification.requestPermission().catch(() => {});
  };

  const handleDismiss = (id, e) => {
    e.stopPropagation();
    persistDismiss(id);
    setDismissed(getDismissedIds());
  };

  return (
    <div ref={panelRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => {
          requestNotifyPermission();
          setOpen((wasOpen) => {
            if (!wasOpen) {
              dismissNotificationIds(items.map((it) => it.id));
              queueMicrotask(() => setDismissed(getDismissedIds()));
            }
            return !wasOpen;
          });
        }}
        aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: 10,
          border: darkMode ? '1px solid rgba(148, 163, 184, 0.45)' : '1px solid rgba(148, 163, 184, 0.5)',
          background: darkMode ? 'rgba(30, 41, 59, 0.85)' : 'rgba(255,255,255,0.85)',
          color: darkMode ? '#e2e8f0' : '#334155',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
          marginTop: 2
        }}
      >
        <BellIcon />
        {count > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              background: '#ef4444',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              border: darkMode ? '2px solid #1e293b' : '2px solid #fff'
            }}
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 320,
            maxHeight: 360,
            overflowY: 'auto',
            background: darkMode ? '#1e293b' : '#fff',
            borderRadius: 12,
            border: darkMode ? '1px solid #475569' : '1px solid #e2e8f0',
            boxShadow: darkMode
              ? '0 12px 40px rgba(0, 0, 0, 0.45)'
              : '0 12px 40px rgba(15, 23, 42, 0.15)',
            zIndex: 200,
            padding: '8px 0'
          }}
        >
          <div
            style={{
              padding: '8px 14px 10px',
              borderBottom: darkMode ? '1px solid #334155' : '1px solid #f1f5f9'
            }}
          >
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: darkMode ? '#f1f5f9' : '#0f172a' }}>
              Notifications
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: darkMode ? '#94a3b8' : '#64748b' }}>
              Reminders 15 min before and when visits or jobs start; accepted requests include date &amp; time.
            </p>
          </div>
          {items.length === 0 ? (
            <p
              style={{
                margin: 0,
                padding: '20px 16px',
                fontSize: 13,
                color: darkMode ? '#94a3b8' : '#64748b',
                textAlign: 'center'
              }}
            >
              You&apos;re all caught up.
            </p>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                style={{
                  padding: '10px 14px',
                  borderBottom: darkMode ? '1px solid #334155' : '1px solid #f8fafc',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start'
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>
                  🔔
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: darkMode ? '#f1f5f9' : '#0f172a' }}>
                    {n.title}
                  </p>
                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: 12,
                      color: darkMode ? '#cbd5e1' : '#475569',
                      lineHeight: 1.35
                    }}
                  >
                    {n.detail}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDismiss(n.id, e)}
                  style={{
                    flexShrink: 0,
                    fontSize: 11,
                    color: darkMode ? '#94a3b8' : '#64748b',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px 4px'
                  }}
                >
                  Dismiss
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
